import type { ReactNode } from 'react';
import {
  AirplaneTakeoffIcon,
  BedIcon,
  BriefcaseIcon,
  BusIcon,
  CarIcon,
  CaretDoubleUpIcon,
  CaretDownIcon,
  CaretUpIcon,
  ChatCircleDotsIcon,
  CheckCircleIcon,
  CircleDashedIcon,
  CircleHalfIcon,
  ClockCountdownIcon,
  EqualsIcon,
  FlagIcon,
  HandTapIcon,
  LinkSimpleIcon,
  PlayCircleIcon,
  ReceiptIcon,
  SmileyIcon,
  SmileyMehIcon,
  SmileyXEyesIcon,
  SparkleIcon,
  XCircleIcon,
  type Icon,
} from '@phosphor-icons/react';

import { twcn } from '@web/ui/core/utils';

export type ToneChipIcon =
  | 'hand-tap'
  | 'chat-circle-dots'
  | 'briefcase'
  | 'clock-countdown'
  | 'link-simple'
  | 'x-circle'
  | 'check-circle'
  | 'circle-half'
  | 'play-circle'
  | 'circle-dashed'
  | 'flag'
  | 'smiley'
  | 'smiley-meh'
  | 'smiley-x-eyes'
  | 'sparkle'
  | 'caret-double-up'
  | 'caret-up'
  | 'equals'
  | 'caret-down'
  | 'airplane'
  | 'bed'
  | 'car'
  | 'bus'
  | 'receipt';

export type ToneChipTone = 'green' | 'amber' | 'red' | 'gray' | 'ai' | 'brand';

const TONE_CLASS: Record<ToneChipTone, string> = {
  green: 'bg-tone-green-tint text-tone-green-ink',
  amber: 'bg-tone-amber-tint text-tone-amber-ink',
  red: 'bg-tone-red-tint text-tone-red-ink',
  gray: 'bg-tone-gray-tint text-tone-gray-ink',
  ai: 'bg-ai-tint text-ai-ink',
  brand: 'bg-primary-tint text-primary-ink',
};

const ICONS: Record<ToneChipIcon, Icon> = {
  'hand-tap': HandTapIcon,
  'chat-circle-dots': ChatCircleDotsIcon,
  briefcase: BriefcaseIcon,
  'clock-countdown': ClockCountdownIcon,
  'link-simple': LinkSimpleIcon,
  'x-circle': XCircleIcon,
  'check-circle': CheckCircleIcon,
  'circle-half': CircleHalfIcon,
  'play-circle': PlayCircleIcon,
  'circle-dashed': CircleDashedIcon,
  flag: FlagIcon,
  smiley: SmileyIcon,
  'smiley-meh': SmileyMehIcon,
  'smiley-x-eyes': SmileyXEyesIcon,
  sparkle: SparkleIcon,
  'caret-double-up': CaretDoubleUpIcon,
  'caret-up': CaretUpIcon,
  equals: EqualsIcon,
  'caret-down': CaretDownIcon,
  airplane: AirplaneTakeoffIcon,
  bed: BedIcon,
  car: CarIcon,
  bus: BusIcon,
  receipt: ReceiptIcon,
};

/**
 * Tone chip — pill with a Phosphor icon + label, locked to the `.chip`
 * spec in the design bundle. 20px tall, 7px side padding, 11px label.
 * Visual decisions live in the table here; the component does not
 * accept a `className` prop.
 */
interface ToneChipProps {
  tone: ToneChipTone;
  icon?: ToneChipIcon;
  /** Mono uppercase label — used for the task state chip. */
  mono?: boolean;
  children: ReactNode;
  title?: string;
}

export function ToneChip({
  tone,
  icon,
  mono,
  children,
  title,
}: Readonly<ToneChipProps>): React.JSX.Element {
  const Cmp = icon ? ICONS[icon] : null;
  return (
    <span
      title={title}
      className={twcn(
        'inline-flex items-center gap-1 rounded-sm px-1.75 h-5 text-card-id font-medium whitespace-nowrap',
        mono && 'font-mono uppercase tracking-eyebrow',
        TONE_CLASS[tone],
      )}
    >
      {Cmp ? <Cmp size={11} weight="regular" aria-hidden /> : null}
      <span>{children}</span>
    </span>
  );
}
